import React, { useContext, useEffect } from 'react';
import { FormControl, Typography, Grid, TextField } from '@mui/material';
import FileInputV3 from '@efex-components/EfexDS/FileInput/FileInput';
import { Controller, useFormContext } from 'react-hook-form';
import { MexicoStakeHoldersFormData } from '@pages/onboarding/Mexico/types/stakeholders.type';
import { useTranslation } from 'react-i18next';
import { addressMaxLength } from '@pages/onboarding/Mexico/constants';
import useEntityType from '@pages/onboarding/Mexico/hooks/useEntityType';
import useEffectOnce from '@main-hooks/useEffectOnce';
import FlexibleAddress from '../../components/FlexibleAddress';
import AddressMx from '../../mex/AddressMx';
import StakeHolderContext from '../context/StakeHolderContext';

interface AddressInfoProps {
  data:
    | (MexicoStakeHoldersFormData & {
        businessSector?: {
          id: number;
        };
      })
    | undefined;
}

const AddressInfo = ({ data }: AddressInfoProps) => {
  const { t } = useTranslation();

  const {
    register,
    control,
    formState: { errors },
    setValue,
  } = useFormContext<MexicoStakeHoldersFormData>();

  const {
    changeProofOfAddressDocument,
    deleteProofOfAddressDocument,
    proofOfAddressDocument,
    personType,
    setProofOfAddressDocument,
    handleDropChangeDocument,
  } = useContext(StakeHolderContext);

  const { isForeign } = useEntityType({
    personType,
  });

  useEffectOnce(() => {
    if (data?.documents?.length) {
      setProofOfAddressDocument(data.documents);
    }
  });

  useEffect(() => {
    if (proofOfAddressDocument && proofOfAddressDocument.length > 0) {
      setValue('proofOfAddress', proofOfAddressDocument, {
        shouldValidate: true,
      });
    } else {
      setValue('proofOfAddress', null, { shouldValidate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proofOfAddressDocument]);

  return (
    <>
      {isForeign ? (
        <FlexibleAddress
          data={data}
          errors={errors}
          control={control}
          register={register}
          setValue={setValue}
        />
      ) : (
        <AddressMx
          errors={errors}
          control={control}
          register={register}
          setValue={setValue}
          data={data}
        />
      )}
      <Grid item xs={12}>
        <FormControl fullWidth>
          <Typography variant="subtitle2">{t(`onbStHlResidence`)}</Typography>
          <TextField
            autoComplete="off"
            type="text"
            className="custom-textfield w-full max-w-[920px]"
            placeholder={t('onbStakeAddress1') || ''}
            error={!!errors.addressLine1}
            helperText={errors.addressLine1?.message as string}
            {...register('addressLine1', {
              maxLength: {
                value: 100,
                message: t('addressTooLong'),
              },
              pattern: {
                value: /^[a-zA-Z0-9\s]+$/,
                message: t('addressInvalidCharacters'),
              },
              validate: {
                noLeadingSpace: (value: string): any =>
                  !value.startsWith(' ') || t('addressStartsWithSpace'),
                noTrailingSpace: (value: string): any =>
                  !value.endsWith(' ') || t('addressEndsWithSpace'),
                noConsecutiveSpaces: (value: string): any =>
                  !/\s\s/.test(value) || t('addressConsecutiveSpaces'),
              },
            })}
            inputProps={{
              maxLength: 100,
              pattern: '[a-zA-Z0-9\\s]+',
              onInput: (e: React.FormEvent<HTMLInputElement>) => {
                const input = e.currentTarget;
                input.value = input.value.replace(/[^a-zA-Z0-9\s]/g, '');
              },
            }}
          />
          {errors && errors.addressLine1 && (
            <p className="text-orange-600 text-xs mt-1">{t('defaultError')}</p>
          )}
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6} className="!my-[15px]">
        <FormControl fullWidth>
          <Typography variant="subtitle2">{t(`externalNumber`)}</Typography>
          <div className="flex w-full">
            <TextField
              fullWidth
              autoComplete="off"
              type="text"
              size="small"
              error={!!errors?.extNumber}
              placeholder="3A"
              {...register('extNumber', {
                required: true,
                maxLength: addressMaxLength,
              })}
            />
          </div>
          {errors?.extNumber && errors?.extNumber.type === 'required' && (
            <p className="text-orange-600 text-xs mt-1">{t('defaultError')}</p>
          )}
          {errors?.extNumber && errors?.extNumber.type === 'maxLength' && (
            <p className="text-orange-600 text-xs mt-1">
              {t('MaxLengthCharacters', {
                max: addressMaxLength,
              })}
            </p>
          )}
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6} className="!my-[15px] !pl-4">
        <FormControl fullWidth>
          <Typography variant="subtitle2">{t(`internalNumber`)}</Typography>
          <div className="flex w-full">
            <TextField
              fullWidth
              autoComplete="off"
              type="text"
              size="small"
              error={!!errors?.intNumber}
              placeholder="Depto 1"
              {...register('intNumber', {
                maxLength: addressMaxLength,
              })}
            />
          </div>
          {errors?.intNumber && errors?.intNumber.type === 'required' && (
            <p className="text-orange-600 text-xs mt-1">{t('defaultError')}</p>
          )}
          {errors?.intNumber && errors?.intNumber.type === 'maxLength' && (
            <p className="text-orange-600 text-xs mt-1">
              {t('MaxLengthCharacters', {
                max: addressMaxLength,
              })}
            </p>
          )}
        </FormControl>
      </Grid>
      <Grid item xs={12} className="!mt-[-20px] !mb-5">
        <Controller
          control={control}
          name="proofOfAddress"
          rules={{ required: true }}
          render={({ field }) => (
            <div>
              <FileInputV3
                isSingleFile
                inputName="proofOfAddress"
                onChange={(e) => {
                  changeProofOfAddressDocument(e);
                  field.onChange(e);
                }}
                onDropChange={handleDropChangeDocument}
                onDelete={() => {
                  deleteProofOfAddressDocument();
                  field.onChange(null);
                }}
                title={t('onbStHlResidenceFile') || ''}
                documents={proofOfAddressDocument}
              />
              {errors.proofOfAddress && (
                <p className="text-orange-600 text-xs mt-1">
                  {t('defaultError')}
                </p>
              )}
            </div>
          )}
        />
      </Grid>
    </>
  );
};

export default AddressInfo;
